import { zodResolver } from "@hookform/resolvers/zod";
import { ActionIcon, Button, LoadingOverlay } from "@mantine/core";
import { ContextModalProps } from "@mantine/modals";
import { WarningIcon } from "@phosphor-icons/react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { z } from "zod";

import { useSession } from "@/hooks/@custom/useSession";
import { MESSAGE } from "@/lib/zod/messages";
import { queueService } from "@/services/queue";

import { Input } from "../../Input";

const generateToken = (digits: number = 4) => {
  const min = Math.pow(10, digits - 1);
  const max = Math.pow(10, digits) - 1;
  return Math.floor(min + Math.random() * (max - min + 1)).toString();
};

export function EditDriver({ context, id }: ContextModalProps) {
  const { driver } = useSession();
  const navigate = useNavigate();

  const driverId = driver?.id;

  type Schema = z.infer<typeof schema>;
  const generatedToken = useMemo(() => generateToken(), []);
  const schema = z
    .object({
      code: z
        .string({ required_error: MESSAGE.GLOBAL.REQUIRED })
        .nonempty({ message: MESSAGE.GLOBAL.NOT_EMPTY })
        .length(4, { message: MESSAGE.GLOBAL.LENGTH(4) }),
      queue: z.object({
        id: z.string().optional()
      })
    })
    .superRefine((val, ctx) => {
      if (val.code !== generatedToken) {
        ctx.addIssue({
          path: ["code"],
          code: z.ZodIssueCode.custom,
          message: "O código não confere."
        });
      }
    });

  const {
    control,
    handleSubmit,
    setFocus,
    reset,
    formState: { errors, isLoading, isSubmitting }
  } = useForm<Schema>({
    mode: "onBlur",
    resolver: zodResolver(schema),
    defaultValues: async () => {
      const defaultData = {
        code: "",
        queue: {
          id: undefined
        }
      };

      try {
        const { data } = await queueService.query.getQueuePositionByDriverId(+driverId!);

        setFocus("code");

        return {
          code: "",
          queue: {
            id: data?.queue.id ? String(data.queue.id) : undefined
          }
        };
      } catch (_error) {
        return defaultData;
      }
    }
  });

  const onSubmit: SubmitHandler<Schema> = async (data) => {
    const { queue } = data;

    if (!queue.id) {
      context.closeModal(id);
      toast.error("Você não está na fila.");
      return;
    }

    await queueService.mutation.deleteQueueById(+queue.id);
    context.closeAll();
    reset();
    await navigate({ to: "/app/perfil/editar", replace: true });
    window.location.reload();
  };

  const handleDeny = () => {
    context.closeModal(id);
  };

  const handleCancelQueueModal = () => {
    setFocus("code");

    const cancelQueueModal = context.openModal({
      withCloseButton: false,
      children: (
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <header className="flex flex-col items-center justify-center gap-2 text-red-500">
            <ActionIcon variant="light" color="red" radius="xl" size="xl">
              <WarningIcon weight="bold" size={24} />
            </ActionIcon>
            <h1 className="text-center text-xl font-semibold text-current">Para editar seu perfil, é necessário sair da fila atual. Deseja continuar?</h1>
          </header>

          <div className="flex flex-col items-center justify-center gap-[inherit]">
            <div className="flex flex-col gap-[inherit]">
              <p className="text-center text-gray-700">
                <strong className="text-xl">Estou ciente e desejo continuar</strong>
                <br />
                Digite o código <span className="text-primary font-bold">{generatedToken}</span> na caixa ao lado
              </p>

              <div className="flex flex-col items-center justify-center gap-1">
                <Input.Pin control={control} name="code" inputMode="numeric" />
                {errors.code && <p className="text-red-500">{errors.code.message}</p>}
              </div>
            </div>

            <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2">
              <Button onClick={() => context.closeModal(cancelQueueModal)} disabled={isSubmitting} variant="outline">
                Cancelar
              </Button>

              <Button type="submit" loading={isSubmitting}>
                Confirmar
              </Button>
            </div>
          </div>
        </form>
      )
    });
  };

  async function checkHasDriverInQueue() {
    try {
      const { data } = await queueService.query.getHasDriverByDriverId(+driverId!);
      // console.log({ data });

      if (!data?.hasDriver) {
        context.closeModal(id);
        navigate({ to: "/app/perfil/editar", replace: true });
      }
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    if (driverId) {
      checkHasDriverInQueue();
    }
  }, [driverId]);

  return (
    <div className="relative flex min-h-80 flex-col justify-between gap-4">
      <LoadingOverlay visible={isLoading} />

      {!isLoading && (
        <>
          <div className="flex flex-col items-center justify-center gap-4">
            <div className="text-primary bg-primary-100 flex size-16 items-center justify-center rounded-full">
              <WarningIcon weight="bold" size={32} />
            </div>

            <div className="flex flex-col items-center justify-center gap-2 text-center">
              <h1 className="text-2xl font-bold text-gray-700">Atenção!</h1>
              <p className="text-xl text-gray-700">Você está em uma fila no momento. Por isso, só é possível editar os métodos de contatos autorizados do seu perfil.</p>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <div className="grid grid-cols-1 gap-[inherit] sm:grid-cols-2">
              <Button onClick={handleDeny}>Cancelar</Button>

              <Button component={Link} onClick={handleDeny} to="app/perfil/editar" variant="outline">
                Prosseguir
              </Button>
            </div>

            <Button onClick={handleCancelQueueModal} color="red">
              Editar mesmo assim
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
